import React, { Suspense, useEffect, useState, useContext } from 'react';
import { ZoneContext, ZONE_INT } from '../context/index';
import InternationalComingSoon from '../screens/international-coming-soon/index';
import { history } from '../store';
import { Layout, NavigationDrawer, TopBar } from '../components';
import Routes from './router';
import AnalyticsTracker from '../AnalyticsTracks';
import { connect } from 'react-redux';
import { accountLogoutAction } from '../redux/actions/accountAction';
import CampaignIcon from '@mui/icons-material/Campaign';
import ExploreIcon from '@mui/icons-material/Explore';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import { ConnectedRouter } from 'connected-react-router';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react';
import config from '../config';
import { getNikName } from '../utils/string';
import { Route, Switch } from 'react-router-dom';
import Waiting from '../screens/waiting-page';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import MyAccount from '../screens/my-account';

const drawerElement = [
  {
    id: 1,
    name: 'Campagne',
    iconName: 'mail-bulk',
    iconType: 'solid',
    icon: (color) => <CampaignIcon style={{ color: color, fontSize: 28 }} />,
    type: 'item',
    path: '/refcampaigns',
    roles: ['local', 'int', 'rec', 'prod'].includes(window._env_?.REACT_APP_ENVIRONMENT) ? ['ROLE_FR'] : [],
  },
  {
    id: 2,
    name: 'Jeux de données',
    iconName: 'user-edit',
    iconType: 'solid',
    icon: (color) => <BubbleChartIcon style={{ color: color, fontSize: 28 }} />,
    type: 'list',
    roles: ['local', 'int', 'rec', 'prod'].includes(window._env_?.REACT_APP_ENVIRONMENT) ? ['ROLE_FR'] : [],
    items: [
      {
        id: 11,
        name: 'Jeux de données Recette',
        path: '/data-sets',
        iconName: 'envelope',
        iconType: 'solid',
        icon: (color) => <DeviceHubIcon style={{ color: color, fontSize: 28 }} />,
        type: 'item',
        roles: ['local', 'int', 'rec', 'prod'].includes(window._env_?.REACT_APP_ENVIRONMENT) ? ['ROLE_FR'] : [],
      },
      {
        id: 10,
        name: 'Jeux de données Production',
        path: '/data-sets-prod',
        iconName: 'chart-line',
        iconType: 'solid',
        icon: (color) => {
          return <Inventory2Icon style={{ color: color, fontSize: 28 }} />;
        },
        type: 'item',
        roles: ['local', 'int', 'rec', 'prod'].includes(window._env_?.REACT_APP_ENVIRONMENT) ? ['ROLE_FR'] : [],
      },
    ],
  },
  {
    id: 6,
    name: 'Pilotage TMK',
    iconName: 'list',
    iconType: 'solid',
    icon: (color) => {
      return <ExploreIcon style={{ color: color, fontSize: 28 }} />;
    },
    type: 'list',
    items: [
      {
        id: 7,
        name: 'Répartition des fiches',
        path: '/volumes',
        iconName: 'chart-pie',
        iconType: 'solid',
        icon: (color) => {
          return <FileCopyIcon style={{ color: color, fontSize: 28 }} />;
        },
        type: 'item',
        roles: ['local', 'int', 'rec', 'prod'].includes(window._env_?.REACT_APP_ENVIRONMENT) ? ['ROLE_FR'] : [],
      },
      {
        id: 8,
        name: 'Volume stop relance',
        path: '/stop-relance',
        iconName: 'stop-circle',
        iconType: 'solid',
        icon: (color) => {
          return <StopCircleIcon style={{ color: color, fontSize: 28 }} />;
        },
        type: 'item',
        roles: ['local', 'int', 'rec', 'prod'].includes(window._env_?.REACT_APP_ENVIRONMENT) ? ['ROLE_FR'] : [],
      },
    ],
  },
  {
    id: 9,
    name: 'Segments',
    path: '/segments',
    iconName: 'chart-pie',
    iconType: 'solid',
    icon: (color) => {
      return <DataUsageIcon style={{ color: color, fontSize: 28 }} />;
    },
    type: 'item',
    roles: ['local', 'int', 'rec', 'prod'].includes(window._env_?.REACT_APP_ENVIRONMENT) ? ['ROLE_FR'] : [],
  },
];

function buildInfoWithLogin(info) {
  const firstNameInitial = info?.given_name?.charAt(0) || '';
  const familyName = info?.family_name || '';

  return {
    ...info,
    login: `${firstNameInitial}${familyName}`.toLowerCase(),
  };
}

function AppRouter(props) {
  const { _accountLogoutAction } = props;
  const [authenticated, setAuthenticated] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const { zone } = useContext(ZoneContext);

  const oktaAuth = new OktaAuth({
    issuer: config.OKTA_URL,
    clientId: config.OKTA_CLIENT_ID,
    redirectUri: `${window.location.origin}/implicit/callback`,
    pkce: false,
    scopes: ['openid', 'profile', 'rfcgroups'],
  });

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const getUserInfo = async (authenticated) => {
    try {
      let info = buildInfoWithLogin(await oktaAuth.token.getUserInfo());
      localStorage.setItem('userLogged', JSON.stringify(info));
      info.roles = getRolesByGroups(info?.groups);

      if (window._env_?.REACT_APP_ENVIRONMENT === 'prod' && window.gtag) {
        window.gtag('set', 'user_properties', {
          role: info.roles?.join(',') || 'unknown',
          zone: localStorage.getItem('zoneId'),
        });
      }

      setTimeout(() => saveUser(info, authenticated), 300);
    } catch (e) {
      //ignore
    }
  };

  const saveUser = (user, authenticated) => {
    setUserInfo({ ...user, authenticated: authenticated });
  };

  const getRolesByGroups = (groups = []) => {
    let roles = [];
    for (let group of groups) {
      roles = [...roles, config.GROUP_TO_ROLES[group]];
    }
    return roles;
  };

  const handleLogout = async () => {
    try {
      _accountLogoutAction();
      localStorage.removeItem('token');
      setUserInfo(null);
      setAuthenticated(false);

      await oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (e) {
      history.push('/');
    }
  };

  const getFirstSelectedPageFromUrl = () => {
    const location = window.location.pathname;
    return getIdFromListByPathRecursive(drawerElement, location);
  };

  const getIdFromListByPathRecursive = (elements, searchedPath) => {
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].type === 'item') {
        if (elements[i].path === searchedPath) {
          return elements[i].id;
        }
      } else {
        let r = getIdFromListByPathRecursive(elements[i].items, searchedPath);
        if (r) return r;
      }
    }
  };

  useEffect(() => {
    oktaAuth.isAuthenticated().then((isAuthenticated) => {
      if (isAuthenticated) {
        const accessToken = oktaAuth.getAccessToken();
        localStorage.setItem('token', accessToken);
        if (userInfo == null && !authenticated) {
          getUserInfo();
        }
      }

      if (isAuthenticated !== authenticated) {
        setAuthenticated(isAuthenticated);
      }
    });
  });

  function hasRoles() {
    return !userInfo?.roles || userInfo?.roles.length === 0;
  }

  return (
    <ConnectedRouter history={history}>
      <AnalyticsTracker />
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <Layout
          left={
            <NavigationDrawer
              data={drawerElement}
              selectedItem={getFirstSelectedPageFromUrl() || 1}
              title={'RFC'}
              selectedColor="#6200ee"
              roles={userInfo?.roles || []}
            />
          }
          right={
            <div>
              <TopBar
                navBarColor="#6200ee"
                logOut={handleLogout}
                avatarName={getNikName(userInfo?.family_name, userInfo?.given_name)}
                userInfo={userInfo}
              />

              <Suspense fallback={<Waiting />}>
                {hasRoles() ? (
                  <Switch>
                    <Route path="/implicit/callback" component={LoginCallback} exact />
                    <SecureRoute component={Waiting} path={'*'} exact />
                  </Switch>
                ) : zone === ZONE_INT ? (
                  <InternationalComingSoon />
                ) : (
                  <Switch>
                    <Route path="/my-account" exact render={(props) => <MyAccount {...props} userInfo={userInfo} />} />
                    <Route path="/" render={() => <Routes roles={userInfo?.roles || []} />} />
                  </Switch>
                )}
              </Suspense>
            </div>
          }
          columnsLeft={1}
          columnsRight={12}
        />
      </Security>
    </ConnectedRouter>
  );
}

const mapStateToProps = (state) => {
  return {
    account: state.account,
    error: state.error,
  };
};
const mapDispatchToProps = {
  _accountLogoutAction: accountLogoutAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppRouter);
